import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Zap } from "lucide-react";
import { MarketingShell } from "@/components/marketing/MarketingShell";
import { BentoCard, BrutalButton } from "@/components/dashboard/BentoCard";

export default function AboutPage() {
  return (
    <MarketingShell>
      <section className="max-w-4xl mx-auto px-6 pt-16 pb-12">
        <div className="inline-flex items-center gap-2 px-3 py-1 border-2 border-black bg-yellow-300 font-mono-data text-[10px] uppercase tracking-widest">
          <ShieldCheck size={12} /> Why Lien Scout exists
        </div>
        <h1 className="mt-6 font-display text-4xl md:text-6xl font-bold tracking-tight leading-[1.05]">
          Tax-sale investing works.<br />
          The <span className="text-red-600">workflow</span> is what's broken.
        </h1>
        <p className="mt-6 text-lg text-zinc-700 leading-relaxed">
          Every month, over four thousand American properties are scheduled for
          tax sale. The lists are technically public. The problem is that
          they're scattered across 3,000+ county websites, each in its own
          format — PDFs, ancient web forms, spreadsheets locked inside zip
          files.
        </p>
        <p className="mt-4 text-lg text-zinc-700 leading-relaxed">
          By the time you've clicked through the third county, you've already
          lost your Saturday. And even after all that clicking, ninety percent
          of what's on the list is junk — mobile homes on rented land,
          contested titles, parcels redeemed the day before the auction.
        </p>
        <p className="mt-4 text-lg text-zinc-700 leading-relaxed">
          Lien Scout was built to solve one problem: <strong>get the shortlist
          in front of you before Saturday morning coffee.</strong> We pull directly
          from primary sources, enforce a top-5-city-per-state whitelist, score
          every listing on equity/condition/urgency, and email you the moment
          new matches hit.
        </p>
        <p className="mt-4 text-lg text-zinc-700 leading-relaxed">
          The list is free. The <strong>shortlist</strong> is what smart people pay for.
        </p>
      </section>

      <section className="max-w-4xl mx-auto px-6 pb-12">
        <div className="grid md:grid-cols-3 gap-4">
          <BentoCard className="!p-5" data-testid="about-value-1">
            <div className="data-label">Value 1</div>
            <div className="font-display text-lg font-bold mt-1">Primary sources only</div>
            <p className="text-sm text-zinc-700 mt-2">
              We fetch directly from county websites, treasurer feeds, and
              official PDFs. No listing-aggregator middlemen.
            </p>
          </BentoCard>
          <BentoCard className="!p-5" data-testid="about-value-2">
            <div className="data-label">Value 2</div>
            <div className="font-display text-lg font-bold mt-1">Radical transparency</div>
            <p className="text-sm text-zinc-700 mt-2">
              Every property row links back to the source URL and shows when it
              was last refreshed. No black boxes.
            </p>
          </BentoCard>
          <BentoCard className="!p-5" data-testid="about-value-3">
            <div className="data-label">Value 3</div>
            <div className="font-display text-lg font-bold mt-1">Investor-friendly pricing</div>
            <p className="text-sm text-zinc-700 mt-2">
              $9.99/mo entry-level. 3-day free trial. Cancel anytime without
              chatting anyone.
            </p>
          </BentoCard>
        </div>
      </section>

      <section className="bg-brand-black text-white">
        <div className="max-w-4xl mx-auto px-6 py-16 text-center">
          <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight">
            Questions? <a href="mailto:sbrown@lienscoutapp.com" className="text-yellow-400 underline decoration-red-500 underline-offset-4">Email me.</a>
          </h2>
          <p className="mt-4 text-white/80">
            I read every message and reply personally. Or use the{" "}
            <Link to="/contact" className="text-yellow-400 hover:underline">
              contact form
            </Link>
            .
          </p>
          <div className="mt-8">
            <BrutalButton as={Link} to="/login" data-testid="about-cta">
              Start free trial <ArrowRight size={14} />
            </BrutalButton>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}
